import { memo, useMemo } from "react";
import { Button, Checkbox, Input, InputNumber, Popover, Select, Space, Tooltip, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, BgColorsOutlined, DeleteOutlined } from "@ant-design/icons";
import { MAX_ROW_DECIMALS, MAX_ROW_INDENT, ROW_KINDS, accountKey, isSpacerRow, sourceKeyOf, toId } from "./reportingUtils";

const filterByLabel = (input, option) =>
  String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

// Widths of the pickers, so blank and heading rows line up with series rows.
const SOURCE_WIDTH = 230;
const TAB_WIDTH = 150;
const ACCOUNT_WIDTH = 210;
const GAP = 8;

// Text fields save when they lose focus (or on Enter), not on every key.
const saveOnBlur = (current, save) => ({
  defaultValue: current,
  onBlur: (e) => e.target.value !== current && save(e.target.value),
  onPressEnter: (e) => e.target.blur(),
});

function RowFormat({ row, handlers, accountName }) {
  const style = row.style;
  const set = (patch) => handlers.setRowStyle(row, patch);
  const formatted =
    style.bold || style.italic || style.indent > 0 || style.lineAbove || !!style.label || style.decimals != null;

  const content = (
    <Space direction="vertical" size={8} style={{ width: 220 }}>
      {style.kind === ROW_KINDS.account && (
        <Input
          key={`label:${style.label}`}
          size="small"
          allowClear
          placeholder={accountName || "Row label"}
          {...saveOnBlur(style.label, (label) => set({ label }))}
        />
      )}
      <Checkbox checked={style.bold} onChange={(e) => set({ bold: e.target.checked })}>
        Bold
      </Checkbox>
      <Checkbox checked={style.italic} onChange={(e) => set({ italic: e.target.checked })}>
        Italic
      </Checkbox>
      <Checkbox checked={style.lineAbove} onChange={(e) => set({ lineAbove: e.target.checked })}>
        Line above (e.g. totals)
      </Checkbox>
      <Space size={8}>
        <span>Indent</span>
        <InputNumber
          size="small"
          min={0}
          max={MAX_ROW_INDENT}
          precision={0}
          value={style.indent}
          onChange={(v) => set({ indent: v ?? 0 })}
        />
      </Space>
      {style.kind === ROW_KINDS.account && (
        <Space size={8}>
          <span>Decimals</span>
          <InputNumber
            size="small"
            min={0}
            max={MAX_ROW_DECIMALS}
            precision={0}
            placeholder="Table"
            value={style.decimals}
            onChange={(v) => set({ decimals: v ?? null })}
          />
        </Space>
      )}
    </Space>
  );

  return (
    <Popover trigger="click" placement="bottomRight" title="Row format" content={content}>
      <Tooltip title="Row format">
        <Button type={formatted ? "link" : "text"} icon={<BgColorsOutlined />} />
      </Tooltip>
    </Popover>
  );
}

// hideSource: custom-column tables, where the columns choose the business
// case/scenario and a series only picks an account.
// rowStyles: the table shows row formatting and blank/heading rows.
function SeriesSelectorRow({ row, isFirst, isLast, ctx, handlers, hideSource = false, rowStyles = false }) {
  const sourceValue = sourceKeyOf(row);
  const accounts = ctx.getAccountsForTab(row.tab_name);
  const accountOptions = useMemo(
    () => accounts.map((a) => ({ value: accountKey(a), label: a.name })),
    [accounts]
  );
  const pending = ctx.isSeriesPending(row);
  const spacer = isSpacerRow(row);
  const spacerWidth = (hideSource ? 0 : SOURCE_WIDTH + GAP) + TAB_WIDTH + GAP + ACCOUNT_WIDTH;

  return (
    <Space wrap size={GAP} style={{ display: "flex", marginBottom: 8 }}>
      {spacer && row.style.kind === ROW_KINDS.heading && (
        <Input
          key={`text:${row.style.text}`}
          placeholder="Heading text"
          style={{ width: spacerWidth }}
          {...saveOnBlur(row.style.text, (text) => handlers.setRowStyle(row, { text }))}
        />
      )}

      {spacer && row.style.kind === ROW_KINDS.blank && (
        <Typography.Text type="secondary" style={{ display: "inline-block", width: spacerWidth }}>
          Blank row
        </Typography.Text>
      )}

      {/* Business case OR scenario, in one picker */}
      {!spacer && !hideSource && (
        <Select
          showSearch
          allowClear
          placeholder="Business case or scenario"
          style={{ width: SOURCE_WIDTH }}
          value={sourceValue}
          options={ctx.sourceOptions}
          filterOption={filterByLabel}
          loading={pending}
          popupMatchSelectWidth={false}
          onChange={(value) => handlers.setSource(row, value)}
        />
      )}

      {!spacer && (
        <Select
          allowClear
          placeholder="Tab"
          style={{ width: TAB_WIDTH }}
          disabled={!hideSource && !sourceValue}
          value={row.tab_name || undefined}
          options={ctx.tabOptions}
          popupMatchSelectWidth={false}
          onChange={(value) => handlers.setTab(row, value)}
        />
      )}

      {!spacer && (
        <Select
          showSearch
          allowClear
          placeholder="Account"
          style={{ width: ACCOUNT_WIDTH }}
          disabled={!row.tab_name}
          value={toId(row.account_id) !== -1 ? accountKey(row) : undefined}
          options={accountOptions}
          filterOption={filterByLabel}
          popupMatchSelectWidth={false}
          onChange={(value) =>
            handlers.setAccount(row, accounts.find((a) => accountKey(a) === value) ?? null)
          }
        />
      )}

      {rowStyles && row.style.kind !== ROW_KINDS.blank && (
        <RowFormat row={row} handlers={handlers} accountName={ctx.accountName(row)} />
      )}

      <Tooltip title="Move up">
        <Button type="text" icon={<ArrowUpOutlined />} disabled={isFirst} onClick={() => handlers.moveSeries(row, -1)} />
      </Tooltip>
      <Tooltip title="Move down">
        <Button type="text" icon={<ArrowDownOutlined />} disabled={isLast} onClick={() => handlers.moveSeries(row, 1)} />
      </Tooltip>

      <Tooltip title={spacer ? "Remove row" : "Remove series"}>
        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handlers.removeSeries(row)} />
      </Tooltip>
    </Space>
  );
}

export default memo(SeriesSelectorRow);
